import { useMemo, useCallback } from "react";

import { Form, Input, Select } from "antd";

import {
    RVFormFloatingLabelWrap,
    BADGE_STATUS,
    RVLink,
    useQueryParamsV6,
    RVFormItem,
    RVStatusSelectOption,
} from "@regal-voice/ui";
import { generateFormItemRequiredRules } from "Components/FormItems/Shared/Validators/InputValidators/InputValidators";
import { useOrchestrationPathing } from "Pages/orchestration/useOrchestrationPathing";
import { useIVRListData } from "ServerData/IVR/useIVRListData/useIVRListData";

import { AgentlessCallFormItemKeys } from "../constants";

import type { QueryParams } from "@regal-voice/ui";

import sharedStyles from "../SharedDemoStyles.module.scss";

const defaultQueryParams: Partial<QueryParams> = {
    page: 1,
    pageSize: 500,
    orderBy: [
        {
            key: "updatedAt",
            value: "DESC",
        },
    ],
};

const ID_LABEL = "IVR Name";

const LINK_TEXT = "Edit IVR";

const REQUIRED_RULE = generateFormItemRequiredRules("IVR");

export function AgentlessCallFormItems(): JSX.Element {
    const form = Form.useFormInstance();
    const { ivrsBasePath } = useOrchestrationPathing();

    const { queryParams } = useQueryParamsV6(defaultQueryParams);
    const { items: ivrs } = useIVRListData({ queryParams });
    const ivrOptions = useMemo(() => {
        return ivrs?.map((ivr) => {
            const { name, state } = ivr;
            const badgeStatus = state === "draft" ? BADGE_STATUS.PENDING : BADGE_STATUS.ACTIVE;
            return {
                label: <RVStatusSelectOption name={name} badgeStatus={badgeStatus} badgeContent={state} />,
                value: ivr.id,
            };
        });
    }, [ivrs]);

    const handleIVRSelect = useCallback(
        (value: string) => {
            const fileName = ivrs?.find((file) => file.id === value)?.name;
            form.setFieldValue(AgentlessCallFormItemKeys.ivrName, fileName);
        },
        [form, ivrs]
    );

    return (
        <>
            <RVFormFloatingLabelWrap
                className={sharedStyles.singleItem}
                showFormLabel
                name={AgentlessCallFormItemKeys.ivrId}
                label={ID_LABEL}
                rules={REQUIRED_RULE}
                extra={<RVLink to={ivrsBasePath} text={LINK_TEXT} />}
            >
                <Select options={ivrOptions} onChange={handleIVRSelect} />
            </RVFormFloatingLabelWrap>
            <RVFormItem name={AgentlessCallFormItemKeys.ivrName} hidden>
                <Input />
            </RVFormItem>
        </>
    );
}
